import { Link, useLocation } from "react-router-dom";
// eslint-disable-next-line import/no-unresolved
import datenschutzMd from "./datenschutz.md?raw";
import { PublicLayout } from "../../components/PublicLayout";
import { useSeo } from "../../lib/seo";

function Wrap({ title, children }: { title: string; children: React.ReactNode }) {
  const { pathname } = useLocation();
  useSeo({ title, path: pathname });
  return (
    <PublicLayout>
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="text-2xl font-semibold mb-6">{title}</h1>
        <div className="legal">{children}</div>
      </div>
    </PublicLayout>
  );
}

/** Minimal markdown → React renderer (headings, lists, paragraphs). */
function SimpleMd({ text }: { text: string }) {
  const lines = text.split("\n");
  const out: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      out.push(<p key={`p-${out.length}`}>{paragraph.join(" ")}</p>);
      paragraph = [];
    }
  };
  const flushList = () => {
    if (listItems.length > 0) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-5">
          {listItems.map((li, i) => (
            <li key={i}>{li}</li>
          ))}
        </ul>,
      );
      listItems = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line === "") {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith("# ")) {
      flushParagraph();
      flushList();
      out.push(
        <h2 key={`h2-${out.length}`}>{line.slice(2)}</h2>,
      );
      continue;
    }
    if (line.startsWith("## ")) {
      flushParagraph();
      flushList();
      out.push(
        <h3 key={`h3-${out.length}`}>{line.slice(3)}</h3>,
      );
      continue;
    }
    if (line.startsWith("- ")) {
      flushParagraph();
      listItems.push(line.slice(2));
      continue;
    }
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return <>{out}</>;
}

export function ImpressumPage() {
  return (
    <Wrap title="Impressum">
      <h2>Angaben gemäß § 5 TMG</h2>
      <p>
        WEGZWEI GmbH
        <br />
        Mozartstraße 2a
        <br />
        52064 Aachen
        <br />
        Deutschland
      </p>

      <h2>Vertretungsberechtigt</h2>
      <p>Geschäftsführer: Maaged Mazyek</p>

      <h2>Kontakt</h2>
      <p>
        E-Mail: info@wegzwei.com
        <br />
        Telefon: +49 (0) 241 – 53 80 83 68
      </p>

      <h2>Umsatzsteuer-Identifikationsnummer</h2>
      <p>Umsatzsteuer-ID gemäß § 27 a UStG: DE306310036</p>

      <h2>Registereintrag</h2>
      <p>
        Handelsregister: HRB 20350
        <br />
        Registergericht: Aachen
      </p>

      <h2>Online-Streitbeilegung</h2>
      <p>
        Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" className="underline">
          https://ec.europa.eu/consumers/odr
        </a>
        . Wir sind nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>
    </Wrap>
  );
}

export function DatenschutzPage() {
  return (
    <Wrap title="Datenschutzerklärung">
      <p className="text-xs text-slate-500">
        petloc wird betrieben durch die WEGZWEI GmbH (siehe Impressum).
      </p>
      <SimpleMd text={datenschutzMd} />
    </Wrap>
  );
}

export function AgbPage() {
  return (
    <Wrap title="Allgemeine Geschäftsbedingungen">
      <p className="text-xs text-slate-500">Stand: {new Date().toLocaleDateString("de-DE")}</p>

      <h2>§ 1 Geltungsbereich und Anbieter</h2>
      <p>
        1.1 Diese Allgemeinen Geschäftsbedingungen („AGB") gelten für sämtliche Verträge, die zwischen
        der WEGZWEI GmbH, Mozartstraße 2a, 52064 Aachen (nachfolgend „Anbieter"), und Verbrauchern
        bzw. Unternehmern (nachfolgend „Kunde") über die Plattform petloc.co geschlossen werden.
      </p>
      <p>
        1.2 Die Plattform petloc vermittelt den Verkauf von QR-Code-Halsbändern für Haustiere sowie
        die Bereitstellung digitaler Dienste zur Auffindbarkeit entlaufener Haustiere, einschließlich
        Benachrichtigungen per E-Mail und SMS.
      </p>
      <p>
        1.3 Abweichende oder entgegenstehende Geschäftsbedingungen des Kunden gelten nur, wenn der
        Anbieter ihnen ausdrücklich schriftlich zustimmt.
      </p>

      <h2>§ 2 Vertragsgegenstand</h2>
      <p>
        2.1 Der Anbieter liefert physische QR-Halsbänder (Sicherheitsverschluss) mit individuellen
        QR-Codes und stellt den digitalen Finder-Dienst unter petloc.co bereit.
      </p>
      <p>
        2.2 Der Finder-Dienst umfasst die öffentliche Anzeige hinterlegter Haustier-Profile beim Scan
        eines QR-Codes, die Entgegennahme von Fundmeldungen (Standort, Nachricht, optional Foto) sowie
        die Weiterleitung dieser Meldungen an den Kunden per E-Mail. Eine zusätzliche Benachrichtigung
        per SMS ist gegen Entgelt möglich.
      </p>

      <h2>§ 3 Vertragsschluss</h2>
      <p>
        3.1 Die Darstellung der Produkte auf petloc.co stellt kein rechtlich bindendes Angebot dar,
        sondern eine unverbindliche Aufforderung zur Abgabe einer Bestellung.
      </p>
      <p>
        3.2 Mit dem Absenden der Bestellung über den Checkout gibt der Kunde ein verbindliches Angebot
        auf Abschluss eines Kaufvertrags ab. Der Vertrag kommt mit Zugang der Bestellbestätigung per
        E-Mail zustande.
      </p>
      <p>
        3.3 Der Vertragstext wird vom Anbieter gespeichert. Der Kunde kann die Bestelldetails jederzeit
        über sein Konto unter petloc.co einsehen.
      </p>

      <h2>§ 4 Preise und Zahlung</h2>
      <p>
        4.1 Alle Preise verstehen sich als Endpreise inklusive der gesetzlichen Mehrwertsteuer (derzeit
        19 %). Etwaige Versandkosten werden im Checkout gesondert ausgewiesen.
      </p>
      <p>
        4.2 Die Zahlung erfolgt über den Zahlungsdienstleister Stripe (Kreditkarte, SEPA, Apple Pay u.a.).
        Die Rechnung wird nach erfolgreicher Zahlung per E-Mail bereitgestellt.
      </p>
      <p>
        4.3 SMS-Benachrichtigungen werden über ein vorausbezahltes Guthaben abgerechnet. Der Preis je
        versendeter SMS beträgt 0,50 € inkl. MwSt. Nicht verbrauchtes Guthaben bleibt auf dem
        Kundenkonto bestehen.
      </p>

      <h2>§ 5 Lieferung und Versand</h2>
      <p>
        5.1 Die Lieferung der QR-Halsbänder erfolgt an die vom Kunden im Checkout angegebene Adresse
        innerhalb Deutschlands, Österreichs und der Schweiz.
      </p>
      <p>
        5.2 Üblicherweise werden Bestellungen innerhalb von 3–7 Werktagen nach Zahlungseingang versandt.
      </p>

      <h2>§ 6 Widerrufsrecht für Verbraucher</h2>
      <p>
        6.1 Verbraucher haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen
        Vertrag zu widerrufen. Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag, an dem der Kunde
        oder ein von ihm benannter Dritter (mit Ausnahme des Beförderers) die Waren in Besitz genommen
        hat.
      </p>
      <p>
        6.2 Um das Widerrufsrecht auszuüben, ist eine eindeutige Erklärung an info@wegzwei.com zu senden
        (z.B. per E-Mail). Zur Wahrung der Widerrufsfrist reicht die rechtzeitige Absendung.
      </p>
      <p>
        6.3 Folgen des Widerrufs: Der Anbieter erstattet alle Zahlungen, die er erhalten hat,
        einschließlich der Lieferkosten (mit Ausnahme zusätzlicher Kosten wegen einer anderen als der
        angebotenen günstigsten Standardlieferung), unverzüglich und spätestens binnen vierzehn Tagen.
      </p>
      <p>
        6.4 Das Widerrufsrecht erlischt für digitale Inhalte (SMS-Guthaben), sobald der Anbieter mit
        der Ausführung begonnen hat und der Kunde ausdrücklich zugestimmt hat.
      </p>

      <h2>§ 7 Digitale Dienste und Verfügbarkeit</h2>
      <p>
        7.1 Der Anbieter bemüht sich um eine möglichst unterbrechungsfreie Verfügbarkeit des
        Finder-Dienstes, schuldet jedoch keine dauerhafte Verfügbarkeit rund um die Uhr. Wartungsarbeiten
        und technische Störungen können kurzfristige Unterbrechungen zur Folge haben.
      </p>
      <p>
        7.2 Der Anbieter behält sich vor, die Funktionalität der Plattform im Rahmen üblicher
        Weiterentwicklung anzupassen.
      </p>

      <h2>§ 8 Pflichten des Kunden</h2>
      <p>
        8.1 Der Kunde ist verpflichtet, bei Registrierung und Anlage eines Haustier-Profils wahrheitsgemäße Angaben zu
        machen und seine Zugangsdaten vertraulich zu behandeln.
      </p>
      <p>
        8.2 Der Kunde stellt sicher, dass eine von ihm angegebene E-Mail-Adresse bzw. Mobilfunknummer
        aktuell und erreichbar ist. Für Folgen einer nicht erreichbaren Kontaktadresse wird keine Haftung
        übernommen.
      </p>

      <h2>§ 9 Gewährleistung</h2>
      <p>
        9.1 Für die gelieferte Hardware gelten die gesetzlichen Gewährleistungsrechte.
      </p>
      <p>
        9.2 Offensichtliche Mängel sind unverzüglich schriftlich anzuzeigen.
      </p>

      <h2>§ 10 Haftung</h2>
      <p>
        10.1 Der Anbieter haftet unbeschränkt bei Vorsatz, grober Fahrlässigkeit sowie bei Verletzung
        von Leben, Körper oder Gesundheit.
      </p>
      <p>
        10.2 Bei leicht fahrlässiger Verletzung wesentlicher Vertragspflichten ist die Haftung auf den
        vertragstypisch vorhersehbaren Schaden begrenzt.
      </p>
      <p>
        10.3 Der Anbieter übernimmt keine Garantie dafür, dass entlaufene Haustiere tatsächlich
        wiedergefunden werden. Die Auffindung hängt maßgeblich vom Verhalten Dritter (Finder) ab.
      </p>
      <p>
        10.4 Die Haftung nach dem Produkthaftungsgesetz bleibt unberührt.
      </p>

      <h2>§ 11 Datenschutz</h2>
      <p>
        11.1 Die Verarbeitung personenbezogener Daten erfolgt gemäß der{" "}
        <Link to="/datenschutz" className="underline">
          Datenschutzerklärung
        </Link>
        .
      </p>
      <p>
        11.2 Standortdaten, die Finder freiwillig übermitteln, werden ausschließlich an den
        jeweiligen Kunden weitergeleitet und nicht für andere Zwecke verwendet.
      </p>

      <h2>§ 12 Kündigung und Löschung</h2>
      <p>
        12.1 Der Kunde kann sein Konto jederzeit über die Einstellungen löschen. Hinterlegte Haustier-Profile
        werden deaktiviert, QR-Scans zeigen eine Info-Seite.
      </p>
      <p>
        12.2 Nicht verbrauchtes SMS-Guthaben verfällt mit Kontolöschung, sofern der Kunde nicht
        ausdrücklich eine Rückerstattung anfordert.
      </p>

      <h2>§ 13 Schlussbestimmungen</h2>
      <p>
        13.1 Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts. Für
        Verbraucher bleibt zwingendes Recht ihres Wohnsitzlandes unberührt.
      </p>
      <p>
        13.2 Ausschließlicher Gerichtsstand für alle Streitigkeiten mit Kaufleuten, juristischen Personen
        des öffentlichen Rechts oder öffentlich-rechtlichen Sondervermögen ist, soweit gesetzlich zulässig,
        Aachen.
      </p>
      <p>
        13.3 Sollten einzelne Bestimmungen dieser AGB unwirksam sein, bleibt die Wirksamkeit der übrigen
        Bestimmungen unberührt.
      </p>
    </Wrap>
  );
}
